import {
  faCode,
  faHome,
  faSave,
  faStar as faStarRegular,
  faStar as faStarSolid,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Chip, Toolbar, ToolbarSeparator } from "@progress/kendo-react-buttons";
import { Window } from "@progress/kendo-react-dialogs";
import { Field, Form, FormElement } from "@progress/kendo-react-form";
import { Loader } from "@progress/kendo-react-indicators";
import { TextBox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Breadcrumb } from "@progress/kendo-react-layout";
import axios from "axios";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import EditPermissionsButton from "../Buttons/EditPermissionsButton.jsx";
import {
  advancedOptionsEnabled,
  allDatasets,
  isAdmin as isAdminSignal,
  isDatasetFavorite,
  notification,
  toggleFavoriteDataset,
  userData,
} from "../Common/Store/CommonSignals.jsx";
import ColorChangingIcon from "../Common/UI/ColorChangingIcon";
import { If } from "../Common/UI/IfComponent";
import { CodeEditor } from "../Common/UI/JsonCodeEditor";
import { validators } from "../Form/validators.js";
import { selectedHamburgerIndex } from "../Navigation/HamburgerMenu/HamburgerMenuSignals.jsx";
import SharedPermissions from "../SharedPermissions/SharedPermissions";
import ShowSharedPerms from "../SharedPermissions/ShowSharedPerms";
import { showDatasetCreationWindow } from "../Template/NewTemplateSignals";
import { userHasAccessToResource } from "../Utilities/AuthorizationUtil";
import { handleSignalRTrigger } from "../Utilities/CommonUtils";
import { datasetTextField } from "./DatasetFields";
import { ListDataset } from "./ListDataset.jsx";
import { datasetData, datasetEditMode, isSaving, isVisible } from "./NewDatasetSignals.jsx";
import { ParameterDataset } from "./ParameterDataset.jsx";
import { UserGroupDataset } from "./UserGroupDataset.jsx";

function NewDataset({ handlePropertyChange }) {
  const userEmail = userData.value?.userEmail;
  selectedHamburgerIndex.value = 4;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const id = params.get("datasetId") || "";
  const navigate = useNavigate();
  const dataset = useSelector(() => {
    return allDatasets.value.find((dataset) => dataset.id === id);
  });
  const isAdmin = isAdminSignal.value;
  const createDatasetVisible = useSelector(() => allDatasets.value.createDatasetVisible);

  // Tags state and handlers
  const [newTagText, setNewTagText] = useState("");

  const handleAddTag = () => {
    if (newTagText.trim() !== "") {
      const currentTags = datasetData.value.tags || [];
      if (!currentTags.includes(newTagText.trim())) {
        datasetData.value = {
          ...datasetData.value,
          tags: [...currentTags, newTagText.trim()],
        };
      }
      setNewTagText("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const currentTags = datasetData.value.tags || [];
    datasetData.value = {
      ...datasetData.value,
      tags: currentTags.filter((tag) => tag !== tagToRemove),
    };
  };

  const validateListData = (listData) => {
    if (datasetData.value.parentDataset) {
      listData.forEach((row) => {
        if (row.filterValue === "" || !row.filterValue) {
          return "Please enter a filter value for each row in the list.";
        }
      });
    }
    listData.forEach((row) => {
      if (row.value === "" || !row.value) {
        return "Please enter a value for each row in the list.";
      }
    });
    return listData.length > 0 ? "" : "Please enter at least one item in the list.";
  };

  const validateDatasetType = (type) => {
    return type ? "" : "Please select a dataset type.";
  };

  const isSubmitDisabled = () => {
    return (
      (datasetData.value.datasetType === "List" &&
        (datasetData.value.values?.length === 0 ||
          datasetData.value.values.some((data) => data.value === ""))) ||
      (datasetData.value.datasetType === "Global Parameters" &&
        (datasetData.value.values?.length === 0 ||
          datasetData.value.values.some((data) => data.value === "") ||
          datasetData.value.values.some((data) => data.filterValue === ""))) ||
      (datasetData.value.datasetType === "User Group" &&
        (datasetData.value.values?.length === 0 ||
          datasetData.value.values.some((data) => !data.person))) ||
      datasetData.value.datasetName === "" ||
      datasetData.value.datasetDescription === "" ||
      datasetData.value.datasetType === ""
    );
  };

  const handleSubmit = () => {
    isSaving.value = true;

    if (isSubmitDisabled()) {
      isSaving.value = false;
      return;
    }

    const dataset = createDatasetObject();
    if (id && id === dataset?.id) {
      updateExistingDataset(dataset);
    } else {
      createNewDataset(dataset);
    }
  };

  const createDatasetObject = () => {
    let cleanedValues = datasetData.value.values;

    if (datasetData.value.datasetType === "User Group") {
      cleanedValues = datasetData.value.values.map(({ person }) => ({
        value: person ? JSON.stringify(person) : "",
        filterValue: null,
      }));
    } else if (
      datasetData.value.datasetType === "List" ||
      datasetData.value.datasetType === "Global Parameters"
    ) {
      cleanedValues = datasetData.value.values.map(({ value, filterValue }) => ({
        value,
        filterValue,
      }));
    }

    return {
      id: id && !createDatasetVisible ? id : uuidv4(),
      owner: dataset?.owner || userEmail,
      datasetName: datasetData.value.datasetName,
      datasetDescription: datasetData.value.datasetDescription,
      datasetType: datasetData.value.datasetType,
      values: cleanedValues,
      parentDataset: datasetData.value?.parentDataset,
      tags: datasetData.value.tags || [],
      coOwners: datasetData.value.coOwners.map((coOwner) => {
        const mapped = { user: coOwner.user };
        if (coOwner.isUserGroup !== undefined) mapped.isUserGroup = coOwner.isUserGroup;
        if (coOwner.userGroupId) mapped.userGroupId = coOwner.userGroupId;
        return mapped;
      }),
      sharedPermission: datasetData.value.sharedPermission.map((sharedPermission) => {
        const mapped = {
          entity: sharedPermission.entity,
          permission: sharedPermission.permission,
        };
        if (sharedPermission.isUserGroup !== undefined)
          mapped.isUserGroup = sharedPermission.isUserGroup;
        if (sharedPermission.userGroupId) mapped.userGroupId = sharedPermission.userGroupId;
        return mapped;
      }),
    };
  };

  async function updateExistingDataset(dataset) {
    await axios.put(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Datasets/${dataset.id}?userId=${userEmail}`,
      dataset
    );
    notification.value = {
      show: true,
      type: "success",
      message: "Dataset updated successfully!",
    };

    await handleSignalRTrigger("UPDATE", "Dataset", dataset);

    allDatasets.value = allDatasets.value.map((d) => (d.id === dataset.id ? dataset : d));

    isSaving.value = false;
    navigate("/datasets");
  }

  async function createNewDataset(dataset) {
    const response = await axios.post(
      `${process.env.REACT_APP_API_ENDPOINT}/api/Datasets?userId=${userEmail}`,
      dataset
    );
    notification.value = {
      show: true,
      type: "success",
      message: "Dataset created successfully!",
    };

    await handleSignalRTrigger("CREATE", "Dataset", response.data);

    allDatasets.value = [...allDatasets.value, response.data];

    isSaving.value = false;
    if (showDatasetCreationWindow.value) {
      showDatasetCreationWindow.value = false;
      const { id, datasetName } = dataset;
      handlePropertyChange({ propName: "Dataset", propValue: { id, datasetName } });
    } else {
      navigate("/datasets");
    }
  }

  const newPermission = (p) => {
    // If adding a User Group, use the updated lists that have individual members removed
    if (p.updatedCoOwners !== undefined || p.updatedSharedPermission !== undefined) {
      datasetData.value = {
        ...datasetData.value,
        coOwners: p.updatedCoOwners !== undefined ? p.updatedCoOwners : datasetData.value.coOwners,
        sharedPermission:
          p.updatedSharedPermission !== undefined
            ? p.updatedSharedPermission
            : datasetData.value.sharedPermission,
      };
    }

    if (p.coOwner) {
      let newCoOwners = [];
      p.coOwner.forEach((coOwner) => {
        newCoOwners.push({
          user: coOwner.user,
          isUserGroup: coOwner.isUserGroup,
          userGroupId: coOwner.userGroupId,
        });
      });
      datasetData.value = {
        ...datasetData.value,
        coOwners: [...datasetData.value.coOwners, ...newCoOwners],
      };
    }
    if (p.sharedPermission) {
      let newPermissions = [];
      p.sharedPermission.forEach((sharedPermission) => {
        newPermissions.push({
          entity: sharedPermission.entity,
          permission: sharedPermission.permission,
          isUserGroup: sharedPermission.isUserGroup,
          userGroupId: sharedPermission.userGroupId,
        });
      });
      datasetData.value = {
        ...datasetData.value,
        sharedPermission: [...datasetData.value.sharedPermission, ...newPermissions],
      };
    }
  };

  const toggleDialog = () => {
    isVisible.value = !isVisible.value;
  };

  function renderDatasetTypeComponents({ formRenderProps }) {
    let component = null;
    switch (datasetData.value.datasetType) {
      case "List":
        component = (
          <ListDataset
            name="listGrid"
            formRenderProps={formRenderProps}
            listData={datasetData.value.values}
            parentDataset={datasetData.value.parentDataset}
          />
        );
        break;
      case "Global Parameters":
        component = (
          <ParameterDataset
            name="parameterGrid"
            formRenderProps={formRenderProps}
            listData={datasetData.value.values}
          />
        );
        break;
      case "User Group":
        component = (
          <UserGroupDataset
            name="userGroupGrid"
            formRenderProps={formRenderProps}
            listData={datasetData.value.values}
          />
        );
        break;
      default:
        component = <div>How did you get here?</div>;
        break;
    }
    return component;
  }

  return (
    <div className="!k-d-flex !k-flex-col !k-justify-content-start !k-rounded-lg !k-w-full !k-h-full">
      <Breadcrumb
        style={{ backgroundColor: "transparent" }}
        data={[
          {
            id: "home",
            icon: (
              <ColorChangingIcon
                icon={faHome}
                initialPrimaryColor="#0078D4"
                initialSecondaryColor="#00aeef"
                initialSize={16}
                hoverSize={16}
              />
            ),
          },
          { id: "viewDatasets_Highlight", text: "View Datasets" },
          {
            id: "newDataset_Edge",
            text:
              id && id === dataset?.id
                ? "Edit " + datasetData.value.datasetName
                : `Create New ${datasetData.value.datasetType}`,
          },
        ]}
        onItemSelect={(e) => {
          console.log(e);
          if (e.id === "home") {
            navigate("/");
          } else if (e.id.includes("viewDatasets")) {
            navigate("/datasets");
          }
        }}
      />
      <div className="k-d-flex k-flex-col k-h-full k-overflow-auto">
        <Toolbar
          size={null}
          className="k-w-full"
        >
          {!isSaving.value && (
            <ColorChangingIcon
              title="Save Dataset"
              name="btnDatasetSubmit"
              icon={faSave}
              initialPrimaryColor="#000"
              initialSecondaryColor="#5fd255"
              primaryOpacity={0.75}
              secondaryOpacity={1}
              initialSize={32}
              hoverSize={32}
              margin="5px 10px"
              onClick={() => handleSubmit()}
            />
          )}
          {isSaving.value && (
            <Loader
              type="converging-spinner"
              themeColor="primary"
              margin="7px 10px"
            />
          )}
          <ToolbarSeparator />
          <div className="aling-right">
            <If condition={advancedOptionsEnabled.value}>
              <div>
                <Button
                  className="!k-fs-xl !k-rounded-0"
                  style={{ padding: "5px 5px 5px 5px" }}
                  fillMode={"flat"}
                  onClick={() => {
                    if (datasetEditMode.value === "Visual") {
                      datasetEditMode.value = "Code";
                    } else {
                      datasetEditMode.value = "Visual";
                    }
                  }}
                >
                  <FontAwesomeIcon
                    icon={faCode}
                    className="k-mr-1"
                    flip="vertical"
                  />
                </Button>
              </div>
            </If>
          </div>
        </Toolbar>
        {datasetEditMode.value === "Visual" && (
          <section className="!k-rounded-bl-xl !k-rounded-br-xl k-bg-white k-p-2 !k-h-full k-overflow-auto k-border-toolbar k-border-solid k-border-1">
            <div className="k-d-flex k-align-items-center k-gap-2">
              <Label style={{ fontSize: "24px" }}>
                <strong>
                  {id && id === dataset?.id
                    ? "Edit " + datasetData.value.datasetName
                    : "Create New " + datasetData.value.datasetType}
                </strong>
              </Label>
              {id && id === dataset?.id && (
                <ColorChangingIcon
                  key={`star-${datasetData.value.id}-${isDatasetFavorite(datasetData.value.id)}`}
                  icon={isDatasetFavorite(datasetData.value.id) ? faStarSolid : faStarRegular}
                  initialPrimaryColor={
                    isDatasetFavorite(datasetData.value.id) ? "#FFA500" : "#999999"
                  }
                  initialSecondaryColor={
                    isDatasetFavorite(datasetData.value.id) ? "#FFD700" : "#979797ff"
                  }
                  initialSize={24}
                  hoverSize={24}
                  tooltip={
                    isDatasetFavorite(datasetData.value.id)
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavoriteDataset(datasetData.value.id);
                  }}
                />
              )}
            </div>
            <EditPermissionsButton
              onClick={toggleDialog}
              name="btnDatasetPermissions"
              hidden={
                !isAdmin &&
                datasetData.value.owner.toLowerCase() !== userEmail.toLowerCase() &&
                !datasetData.value.coOwners.some((co) => {
                  // Individual co-owner
                  if (!co.isUserGroup && co.user.toLowerCase() === userEmail.toLowerCase()) {
                    return true;
                  }
                  // User Group co-owner - check membership
                  if (co.isUserGroup && co.userGroupId) {
                    return userHasAccessToResource({ coOwners: [co] }, userEmail);
                  }
                  return false;
                })
              }
            />
            <ShowSharedPerms
              isTemplate={false}
              owner={datasetData.value.owner}
              coOwners={datasetData.value.coOwners}
              sharedPermission={datasetData.value.sharedPermission}
            />
            <Form
              validation={{
                listGrid: (value) => validateListData(value),
                parameterGrid: (value) => validateListData(value),
                userGroupGrid: (value) => validateListData(value),
                datasetType: (value) => validateDatasetType(value),
              }}
              initialValues={{
                datasetName: datasetData.value.datasetName,
                datasetDescription: datasetData.value.datasetDescription,
                parentDataset: datasetData.value.parentDataset,
              }}
              render={(formRenderProps) => (
                <FormElement>
                  <fieldset className={"k-form-fieldset"}>
                    <Field
                      className={
                        datasetData.value.datasetName
                          ? "!k-valid !k-w-1/3 !k-h-9"
                          : "!k-invalid !k-w-1/3 !k-h-9"
                      }
                      id={"datasetName"}
                      name="txtDatasetName"
                      label={"Dataset Name"}
                      component={datasetTextField}
                      defaultValue={datasetData.value.datasetName}
                      optional={true}
                      validator={validators.required}
                      onChange={(e) =>
                        (datasetData.value = { ...datasetData.value, datasetName: e.target.value })
                      }
                    />
                    <Field
                      className={
                        datasetData.value.datasetDescription
                          ? "!k-valid !k-w-1/3 !k-h-9"
                          : "!k-invalid !k-w-1/3 !k-h-9"
                      }
                      id={"datasetDescription"}
                      name="txtDatasetDescription"
                      label={"Dataset Description"}
                      component={datasetTextField}
                      defaultValue={datasetData.value.datasetDescription}
                      optional={true}
                      validator={validators.required}
                      onChange={(e) =>
                        (datasetData.value = {
                          ...datasetData.value,
                          datasetDescription: e.target.value,
                        })
                      }
                    />
                    <div className="!k-w-1/3 k-mt-2">
                      <Label>Tags</Label>
                      <div style={{ display: "flex", gap: "5px" }}>
                        <TextBox
                          value={newTagText}
                          onChange={(e) => setNewTagText(e.target.value)}
                          placeholder="Add a tag"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleAddTag();
                            }
                          }}
                        />
                        <Button
                          themeColor="primary"
                          onClick={handleAddTag}
                          disabled={!newTagText.trim()}
                        >
                          Add Tag
                        </Button>
                      </div>
                      {(datasetData.value.tags || []).length > 0 && (
                        <div
                          className="k-mt-2 k-d-flex k-flex-wrap"
                          style={{ gap: "8px" }}
                        >
                          {(datasetData.value.tags || []).map((tag, index) => (
                            <Chip
                              key={tag + "_" + index}
                              text={tag}
                              removable={true}
                              onRemove={() => handleRemoveTag(tag)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {renderDatasetTypeComponents({ formRenderProps })}
                  </fieldset>
                </FormElement>
              )}
            />
            {isVisible.value && (
              <Window
                onClose={toggleDialog}
                initialHeight={900}
                initialWidth={900}
                title={"New Dataset Permissions"}
              >
                <SharedPermissions
                  isDataset={true}
                  coOwners={datasetData.value.coOwners}
                  sharedPermission={datasetData.value.sharedPermission}
                  visible={isVisible.value}
                  removeCoOwner={(coOwner) => {
                    const removedCoOwners = datasetData.value.coOwners.filter(
                      (c) => c.user !== coOwner.user
                    );
                    datasetData.value = { ...datasetData.value, coOwners: removedCoOwners };
                  }}
                  removeSharedPermission={(x) => {
                    const removedSharedPermissions = datasetData.value.sharedPermission.filter(
                      (u) => u.entity !== x.entity
                    );
                    datasetData.value = {
                      ...datasetData.value,
                      sharedPermission: removedSharedPermissions,
                    };
                  }}
                  closeWindow={(e) => {
                    isVisible.value = false;
                    newPermission(e);
                  }}
                />
              </Window>
            )}{" "}
          </section>
        )}
        {datasetEditMode.value === "Code" && (
          <CodeEditor
            initialCode={datasetData.value}
            language={"json"}
            type={"Dataset"}
          />
        )}
      </div>
    </div>
  );
}

export default NewDataset;
